import { haversineMeters } from './useLocationTracking';

// Pure geometry for Area Search Coordinator's optional grid search mode — no
// Firestore/React imports. Cells are square, aligned to a bounding-box grid
// derived from the search boundary; only cells whose center falls inside the
// actual circle/polygon are kept ("in scope"). Not survey-grade — same
// degree-per-meter approximation already used elsewhere in this tool (see
// `boundaryExtentLatLngs` in AreaSearch.js).

const METERS_PER_DEGREE_LAT = 111320;

export function pointInCircle(point, center, radiusMeters) {
  return haversineMeters(point, center) <= radiusMeters;
}

// Ray-casting point-in-polygon test (odd/even crossing count) — hand-rolled
// since no geometry library is installed and this codebase's precedent is to
// hand-roll narrow geo needs rather than add a dependency for them.
export function pointInPolygon(point, points) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].lng, yi = points[i].lat;
    const xj = points[j].lng, yj = points[j].lat;
    const intersects = ((yi > point.lat) !== (yj > point.lat))
      && (point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}

// Spreadsheet-style column letters: 0->A, 1->B, ..., 25->Z, 26->AA, ...
export function colLetters(col) {
  let n = col + 1;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

// Human-readable, radio-friendly cell code, e.g. "B4".
export function gridCellCode(row, col) {
  return `${colLetters(col)}${row + 1}`;
}

export function cellDocId(row, col) {
  return `r${row}_c${col}`;
}

function boundaryBoundingBox(boundary) {
  if (boundary.type === 'circle' && boundary.center && boundary.radiusMeters) {
    const { lat, lng } = boundary.center;
    const latDelta = boundary.radiusMeters / METERS_PER_DEGREE_LAT;
    const lngDelta = boundary.radiusMeters / (METERS_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180) || 1);
    return { minLat: lat - latDelta, maxLat: lat + latDelta, minLng: lng - lngDelta, maxLng: lng + lngDelta };
  }
  if (boundary.type === 'polygon' && Array.isArray(boundary.points) && boundary.points.length) {
    const lats = boundary.points.map(p => p.lat), lngs = boundary.points.map(p => p.lng);
    return { minLat: Math.min(...lats), maxLat: Math.max(...lats), minLng: Math.min(...lngs), maxLng: Math.max(...lngs) };
  }
  return null;
}

// Guards against a huge-area + tiny-cell combo freezing the tab during the
// O(rows*cols) point-in-shape pass, before even getting to the maxCells check.
const RAW_CELL_CEILING = 20000;

// Subdivides a boundary's bounding box into cellSizeMeters-square cells,
// keeping only in-scope cells. Returns {ok:true, cells, rows, cols} or
// {ok:false, reason:'too_many_cells', count} — a result object, not a throw,
// so the caller can show a clear "increase cell size or shrink the area"
// message instead of crashing.
export function computeGridCells(boundary, cellSizeMeters, { maxCells = 2500 } = {}) {
  if (!boundary || !cellSizeMeters || cellSizeMeters <= 0) return { ok: true, cells: [], rows: 0, cols: 0 };
  const bbox = boundaryBoundingBox(boundary);
  if (!bbox) return { ok: true, cells: [], rows: 0, cols: 0 };

  const bboxCenterLat = (bbox.minLat + bbox.maxLat) / 2;
  const latStep = cellSizeMeters / METERS_PER_DEGREE_LAT;
  const lngStep = cellSizeMeters / (METERS_PER_DEGREE_LAT * Math.cos((bboxCenterLat * Math.PI) / 180) || 1);

  const rows = Math.max(1, Math.ceil((bbox.maxLat - bbox.minLat) / latStep));
  const cols = Math.max(1, Math.ceil((bbox.maxLng - bbox.minLng) / lngStep));

  if (rows * cols > RAW_CELL_CEILING) {
    return { ok: false, reason: 'too_many_cells', count: rows * cols };
  }

  const inScope = boundary.type === 'circle'
    ? p => pointInCircle(p, boundary.center, boundary.radiusMeters)
    : p => pointInPolygon(p, boundary.points || []);

  const cells = [];
  for (let row = 0; row < rows; row++) {
    const south = bbox.minLat + row * latStep;
    const north = south + latStep;
    for (let col = 0; col < cols; col++) {
      const west = bbox.minLng + col * lngStep;
      const east = west + lngStep;
      const centerLat = (south + north) / 2;
      const centerLng = (west + east) / 2;
      if (!inScope({ lat: centerLat, lng: centerLng })) continue;
      cells.push({
        row, col, code: gridCellCode(row, col),
        centerLat, centerLng,
        boundsSouth: south, boundsWest: west, boundsNorth: north, boundsEast: east,
      });
      if (cells.length > maxCells) return { ok: false, reason: 'too_many_cells', count: rows * cols };
    }
  }

  return { ok: true, cells, rows, cols };
}

// Suggests a starting cell size for a given boundary: 5m by default, scaled
// up only as much as needed to keep the in-scope cell count under `maxCells`
// for boundaries too large for 5m cells. Used to seed the create-screen's
// cell-size field so a big boundary doesn't just show an error at the 5m
// default — it's still a plain, user-editable value afterward, this only
// picks a sensible starting point.
export function suggestCellSize(boundary, { minSize = 5, maxCells = 2500, maxSize = 500 } = {}) {
  if (!boundary) return minSize;

  // Geometric search: scale size up based on how far over the cap the count
  // is, converging in a few iterations rather than stepping meter-by-meter.
  let size = minSize;
  for (let i = 0; i < 8; i++) {
    const result = computeGridCells(boundary, size, { maxCells: 20000 });
    const count = result.ok ? result.cells.length : result.count;
    if (result.ok && count <= maxCells) break;
    if (!count || size >= maxSize) break;
    size = Math.min(maxSize, size * Math.sqrt(count / maxCells) * 1.08);
  }

  // Round up to a clean multiple of 5 for a friendlier number, then verify
  // (and nudge further if needed) — rounding/search imprecision could
  // theoretically leave it just over the cap.
  size = Math.min(maxSize, Math.ceil(size / 5) * 5);
  for (let i = 0; i < 20 && size < maxSize; i++) {
    const result = computeGridCells(boundary, size, { maxCells });
    if (result.ok) break;
    size = Math.min(maxSize, size + 5);
  }

  return Math.max(minSize, size);
}

// Single source of truth for a cell's coverage tier — used identically by the
// live map, the Grid Reference list, and the PDF export so they can never
// disagree on a cell's color/status.
export function computeCellTier(cell) {
  if (cell.manualOverride === true) return 'confirmed';
  const hits = cell.autoHitTrailPointIds ? Object.keys(cell.autoHitTrailPointIds).length : 0;
  if (hits >= 2) return 'confirmed';
  if (hits === 1) return 'possible';
  return 'none';
}
